<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
});

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));

const chartData = computed(() => props.data?.data || []);

const maxValue = computed(() => {
  if (!chartData.value.length) return 1;
  const maxWon = Math.max(...chartData.value.map(d => d.won || 0));
  const maxLost = Math.max(...chartData.value.map(d => d.lost || 0));
  return Math.max(maxWon, maxLost, 1);
});

const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(browserLocale.value, {
    month: 'short',
    day: 'numeric',
  });
};

const getBarHeight = value => {
  const percentage = (value / maxValue.value) * 100;
  return `${Math.max(percentage, 2)}%`;
};

const totals = computed(() => {
  const data = chartData.value;
  return {
    won: data.reduce((sum, d) => sum + (d.won || 0), 0),
    lost: data.reduce((sum, d) => sum + (d.lost || 0), 0),
    value: data.reduce((sum, d) => sum + (d.value || 0), 0),
  };
});
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
        {{ t('DEAL_REPORTS.TIMELINE.TITLE') }}
      </h3>
      <div class="flex items-center gap-4 text-sm">
        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded-full bg-green-500" />
          <span class="text-slate-600 dark:text-slate-400">
            {{ t('DEAL_REPORTS.TIMELINE.WON') }} ({{ totals.won }})
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-3 h-3 rounded-full bg-red-500" />
          <span class="text-slate-600 dark:text-slate-400">
            {{ t('DEAL_REPORTS.TIMELINE.LOST') }} ({{ totals.lost }})
          </span>
        </div>
      </div>
    </div>

    <!-- Chart -->
    <div v-if="chartData.length" class="h-64 flex items-end gap-2">
      <div
        v-for="(item, index) in chartData"
        :key="index"
        class="flex-1 flex flex-col items-center gap-1"
      >
        <div class="flex-1 w-full flex items-end gap-1">
          <!-- Won bar -->
          <div
            class="flex-1 bg-green-500 rounded-t transition-all duration-300"
            :style="{ height: getBarHeight(item.won) }"
            :title="`${t('DEAL_REPORTS.TIMELINE.WON')}: ${item.won}`"
          />
          <!-- Lost bar -->
          <div
            class="flex-1 bg-red-500 rounded-t transition-all duration-300"
            :style="{ height: getBarHeight(item.lost) }"
            :title="`${t('DEAL_REPORTS.TIMELINE.LOST')}: ${item.lost}`"
          />
        </div>
        <span
          class="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
        >
          {{ formatDate(item.date) }}
        </span>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="h-64 flex items-center justify-center">
      <p class="text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.NO_DATA') }}
      </p>
    </div>

    <!-- Summary -->
    <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
      <div class="flex justify-between text-sm">
        <span class="text-slate-600 dark:text-slate-400">
          {{ t('DEAL_REPORTS.TIMELINE.TOTAL_VALUE') }}
        </span>
        <span class="font-semibold text-slate-900 dark:text-white">
          {{ formatCurrency(totals.value) }}
        </span>
      </div>
    </div>
  </div>
</template>
